import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, X, RefreshCw, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "My Recent OTPs" — a self-recovery dialog for the staff so they can
 * always retrieve any OTP generated today, even if the original
 * check-in/out/break/visit modal never showed (network drop / phone
 * background / double-tap duplicate branch).
 *
 * Fetches ``GET /api/attendance/recent-otps`` and lists every gate
 * movement OTP for today, newest first.
 *
 * Motivation: Jul 2, 2026 user report — 3 staff (SAKTHIVEL / Jayasurya /
 * BOOPATHI) missed their morning check-in OTP because their app either
 * double-tapped or the WebView backgrounded before the OTP modal
 * rendered. Even though the OTP was stored server-side, the staff had
 * no way to retrieve it from the app.
 */

const TYPE_LABEL = {
  checkin: 'Check-in',
  checkout: 'Check-out',
  break: 'Break',
  visit: 'Visit',
  non_plan: 'Unplanned',
  permission: 'Permission',
};

function fmtClock(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function RecentOtpsModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [otps, setOtps] = useState([]);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/attendance/recent-otps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setOtps(body?.otps || []);
    } catch (e) {
      setErr(e?.message || 'Failed to load OTPs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="recent-otps-modal" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white px-5 py-4 relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-full"
            data-testid="recent-otps-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-2 mb-1 text-white/90 text-xs uppercase tracking-wider font-semibold">
            <KeyRound size={14} /> Today&apos;s OTPs
          </div>
          <p className="text-white/80 text-sm">Every gate OTP you generated today. Newest first.</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="text-center py-8 text-gray-500 text-sm">Loading…</div>
          )}
          {err && !loading && (
            <div className="m-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="recent-otps-error">{err}</div>
          )}
          {!loading && !err && otps.length === 0 && (
            <div className="text-center py-10 text-gray-500 text-sm px-4" data-testid="recent-otps-empty">
              No OTPs generated today yet. Once you check in, they&apos;ll appear here.
            </div>
          )}
          {otps.map((row, idx) => (
            <div
              key={`${row.short_code}-${idx}`}
              className="px-5 py-4 border-b border-gray-100 last:border-b-0 flex items-center gap-3"
              data-testid={`recent-otp-row-${idx}`}
            >
              <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center shrink-0">
                <ShieldCheck size={20} className="text-indigo-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {TYPE_LABEL[row.type] || row.type}
                  </span>
                  {!row.gate_control_enabled && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
                      no gate
                    </span>
                  )}
                  <span className="text-xs text-gray-400 ml-auto">{fmtClock(row.at)}</span>
                </div>
                <div className="text-2xl font-bold text-indigo-700 tabular-nums tracking-widest select-all">
                  {row.otp}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t p-3 flex gap-2 bg-gray-50">
          <button
            onClick={load}
            disabled={loading}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold"
            data-testid="recent-otps-refresh"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-semibold"
            data-testid="recent-otps-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
